import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Newspaper, 
  Clock, 
  ExternalLink,
  Loader2,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default news articles for when DB is empty
const defaultNews = [
  {
    id: '1',
    title: 'Fog Reveal: Law Enforcement\'s Secret Surveillance Tool Exposed',
    summary: 'Investigation reveals how police agencies across the US are using a little-known tool to track citizens without warrants.',
    content: 'Full article content...',
    category: 'surveillance',
    image_url: 'https://images.unsplash.com/photo-1586449480584-34302e933441?w=600&q=80',
    source_url: 'https://apnews.com',
    created_at: new Date().toISOString()
  },
  {
    id: '2',
    title: 'Supreme Court May Revisit Digital Privacy Protections',
    summary: 'Legal experts predict upcoming cases could redefine Fourth Amendment protections in the digital age.',
    content: 'Full article content...',
    category: 'legal',
    image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80',
    source_url: '#',
    created_at: new Date(Date.now() - 86400000).toISOString()
  },
  {
    id: '3',
    title: 'New Privacy Bill Gains Momentum in Congress',
    summary: 'ADPPA could fundamentally change how companies collect and use your personal data.',
    content: 'Full article content...',
    category: 'legislation',
    image_url: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=600&q=80',
    source_url: '#',
    created_at: new Date(Date.now() - 172800000).toISOString()
  },
  {
    id: '4',
    title: 'Data Brokers: The Hidden Industry Trading Your Information',
    summary: 'How billions of data points about Americans are bought and sold without their knowledge.',
    content: 'Full article content...',
    category: 'privacy',
    image_url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80',
    source_url: '#',
    created_at: new Date(Date.now() - 259200000).toISOString()
  },
  {
    id: '5',
    title: 'Apple and Google Face Pressure Over Location Tracking',
    summary: 'Lawmakers demand answers about how mobile operating systems handle location data.',
    content: 'Full article content...',
    category: 'tech',
    image_url: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80',
    source_url: '#',
    created_at: new Date(Date.now() - 345600000).toISOString()
  },
  {
    id: '6',
    title: 'VPN Usage Surges Amid Privacy Concerns',
    summary: 'More Americans are turning to VPNs as awareness of surveillance practices grows.',
    content: 'Full article content...',
    category: 'tools',
    image_url: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=600&q=80',
    source_url: '#',
    created_at: new Date(Date.now() - 432000000).toISOString()
  }
];

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data.length > 0 ? response.data : defaultNews);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setNews(defaultNews);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All News' },
    { id: 'surveillance', label: 'Surveillance' },
    { id: 'legal', label: 'Legal' },
    { id: 'legislation', label: 'Legislation' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'tech', label: 'Tech' },
    { id: 'tools', label: 'Tools' },
  ];

  const filteredNews = activeCategory === 'all' 
    ? news 
    : news.filter(n => n.category === activeCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="news-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-[#00FF94]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">
                Privacy News
              </h1>
              <p className="text-gray-500">
                Stay informed about surveillance, privacy laws, and digital rights
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-[#00FF94] text-black'
                  : 'bg-[#0a0a0a] border border-[#262626] text-gray-400 hover:text-white hover:border-[#00FF94]/30'
              }`}
              data-testid={`news-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((article, index) => (
            <Card 
              key={article.id}
              className="bg-[#0a0a0a] border-[#262626] overflow-hidden group card-hover"
              data-testid={`news-card-${index}`}
            >
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={article.image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80'} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                <Badge className="absolute top-3 right-3 bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize">
                  {article.category}
                </Badge>
              </div>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(article.created_at)}</span>
                </div>
                <h3 className="font-heading font-semibold text-white text-lg mb-2 line-clamp-2 group-hover:text-[#00FF94] transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-500 text-sm line-clamp-2 mb-4">
                  {article.summary}
                </p>
                {article.source_url && article.source_url !== '#' && (
                  <a 
                    href={article.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-[#00FF94] transition-colors"
                  >
                    Read full article
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredNews.length === 0 && (
          <div className="text-center py-16">
            <Newspaper className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No news in this category</h3>
            <p className="text-gray-500 text-sm">Check back later for updates</p>
          </div>
        )}
      </div>
    </div>
  );
}
